import React, { useState, useRef, useEffect } from "react";
import { useFiltersStore } from "../../stores/filters.store";
import { useJobsStore } from "../../stores/jobs.store";
import { globalConfig } from "../../config/globalConfig";
import { useResources } from "../../hooks/useResources";
import Pagination from "../pagination/Pagination";
import type { SelectOption } from "../../interfaces/componentInterfaces/dropdown";

type JobType = "create" | "update";

interface TableFilterProps {
  jobType: JobType;
}

const TableFilter: React.FC<TableFilterProps> = ({ jobType }) => {
  const { getResourceValue } = useResources();

  const { 
    selectedPendingGraphics, 
    setSelectedPendingGraphics,
    pageSize,
    setPageSize,
    setCurrentPage,
    searchText,
    setSearchText,
    searchType,
    setSearchType,
    selectedGradeId,
    selectedSubjectId,
    selectedCountryIsoCode,
    selectedLanguageIsoCode,
  } = useFiltersStore();

  const { pagination, load: loadJobs } = useJobsStore();

  const [searchInput, setSearchInput] = useState(searchText || "");
  const [isGraphicsOpen, setIsGraphicsOpen] = useState(false);
  const [isSearchTypeOpen, setIsSearchTypeOpen] = useState(false);

  const graphicsRef = useRef<HTMLDivElement>(null);
  const searchTypeRef = useRef<HTMLDivElement>(null);

  // Sync local search input with store value
  useEffect(() => {
    setSearchInput(searchText || "");
  }, [searchText,searchType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (graphicsRef.current && !graphicsRef.current.contains(event.target as Node)) {
        setIsGraphicsOpen(false);
      }
      if (searchTypeRef.current && !searchTypeRef.current.contains(event.target as Node)) {
        setIsSearchTypeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const trimmedValue = searchInput.trim();
      setSearchText(trimmedValue || null);

      loadJobs({
        page_number: globalConfig.defaultPageNumber,
        page_size: pageSize,
        grade_id: selectedGradeId,
        subject_id: selectedSubjectId,
        country_iso_code: selectedCountryIsoCode,
        language_iso_code: selectedLanguageIsoCode,
        pending_graphics: selectedPendingGraphics,
        search_text: trimmedValue || null,
        search_type: searchType,
        job_type: jobType,
      });
      
      // Reset to first page when searching
      setCurrentPage(globalConfig.defaultPageNumber);
    }
  };

  const handlePendingGraphicsSelect = (value: string) => {
    setSelectedPendingGraphics(value === '' ? null : value === 'true');
    setIsGraphicsOpen(false);
  };

  const handleSearchTypeChange = (value: string) => {
    setSearchType(value || null);
    setSearchText(null);
    setSearchInput("");
    setIsSearchTypeOpen(false);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const graphicsOptions = [
    { value: '', label: getResourceValue("all_questions", "All Questions") },
    { value: 'true', label: getResourceValue("requires_graphics", "Requires Graphics") },
  ];

  const searchTypeOptions: SelectOption[] = [
    { value: 'JobId', label: getResourceValue("search_by_job_id", "Job ID") },
    { value: 'Username', label: getResourceValue("search_by_useremail", "User Email") },
    { value: 'QuestionId', label: getResourceValue("search_by_question_id", "Question ID") },
  ];

  const currentGraphicsLabel = graphicsOptions.find(opt => 
    (selectedPendingGraphics === null && opt.value === '') || 
    (selectedPendingGraphics !== null && opt.value === selectedPendingGraphics.toString())
  )?.label;

  const currentSearchTypeLabel = searchTypeOptions.find(opt => 
    (searchType ?? 'JobId') === opt.value
  )?.label;

  return (
    <div className="table-filter-container">
      {/* Search Section */}
      <div className="search-section">
        <div className="search-container">
          <i className="f-icon search-icon"></i>
          <input
            type="text"
            placeholder={getResourceValue("search_placeholder", " Job ID, Question ID, or Email...")}
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="search-input"
          />
        </div>
      </div>

      {/* Sorting Section */}
      <div className="sorting-section">
        <div className="sorting-controls">
          {/* Search Type DDL */}
          <div className="filter-dropdown" ref={searchTypeRef}>
            <div 
              className={`filter-dropdown-content ${isSearchTypeOpen ? 'open' : ''}`}
              onClick={() => setIsSearchTypeOpen(!isSearchTypeOpen)}
            >
              <div className="filter-icon-wrapper">
                <i className="f-icon bars-sort-icon fa-regular"></i>
              </div>
              <div className="filter-dropdown-trigger">
                <span>{currentSearchTypeLabel}</span>
                <i className="f-icon chevron-down-icon"></i>
              </div>
            </div>
            {isSearchTypeOpen && (
              <div className="filter-dropdown-menu">
                {searchTypeOptions.map((option) => (
                  <div 
                    key={option.value} 
                    className="filter-dropdown-item"
                    onClick={() => handleSearchTypeChange(option.value)}
                  >
                    <span>{option.label}</span>
                    {(searchType ?? 'JobId') === option.value && (
                      <i className="f-icon check-icon fa-solid fa-check"></i>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Requires Graphics Filter */}
          <div className="filter-dropdown" ref={graphicsRef}>
            <div 
              className={`filter-dropdown-content ${isGraphicsOpen ? 'open' : ''}`}
              onClick={() => setIsGraphicsOpen(!isGraphicsOpen)}
            >
              <div className="filter-icon-wrapper">
                <i className="f-icon filter-icon fa-regular"></i>
              </div>
              <div className="filter-dropdown-trigger">
                <span>{currentGraphicsLabel}</span>
                <i className="f-icon chevron-down-icon"></i>
              </div>
            </div>
            {isGraphicsOpen && (
              <div className="filter-dropdown-menu">
                {graphicsOptions.map((option) => (
                  <div 
                    key={option.value} 
                    className="filter-dropdown-item"
                    onClick={() => handlePendingGraphicsSelect(option.value)}
                  >
                    <span>{option.label}</span>
                    {((selectedPendingGraphics === null && option.value === '') || 
                      (selectedPendingGraphics !== null && option.value === selectedPendingGraphics.toString())) && (
                      <i className="f-icon check-icon fa-solid fa-check"></i>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Pagination Component */}
          <Pagination
            currentPage={pagination?.page_number ?? globalConfig.defaultPageNumber}
            totalPages={pagination?.total_pages ?? 1}
            totalRecords={pagination?.total_records ?? 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </div>
  );
};

export default TableFilter;
